import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SapExpertise from './expertise/SapExpertise';
import SalesforceExpertise from './expertise/SalesforceExpertise';
import DynamicsExpertise from './expertise/DynamicsExpertise';

// Simple Arrow Icons (Replace with better icons if available)
const ChevronLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
    </svg>
);

const ChevronRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
    </svg>
);

const expertiseComponents = [
    { id: 'sap', Component: SapExpertise },
    { id: 'salesforce', Component: SalesforceExpertise },
    { id: 'dynamics', Component: DynamicsExpertise },
];

function ExpertiseCarousel() {
    const [activeIndex, setActiveIndex] = useState(0);
    const [direction, setDirection] = useState(0);
    const carouselRef = useRef(null);

    const setIndex = (newIndex, newDirection) => {
        if (newIndex < 0) {
            newIndex = expertiseComponents.length - 1;
        } else if (newIndex >= expertiseComponents.length) {
            newIndex = 0;
        }
        setDirection(newDirection);
        setActiveIndex(newIndex);

        if (carouselRef.current) {
            carouselRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const navigate = (navDirection) => {
        setIndex(activeIndex + navDirection, navDirection);
    };

    const goToIndex = (newIndex) => {
        if (newIndex === activeIndex) return;
        const newDirection = newIndex > activeIndex ? 1 : -1;
        setIndex(newIndex, newDirection);
    };

    const { Component } = expertiseComponents[activeIndex];

    const variants = {
        enter: (direction) => {
            return {
                x: direction > 0 ? 1000 : -1000,
                opacity: 0
            };
        },
        center: {
            zIndex: 1,
            x: 0,
            opacity: 1
        },
        exit: (direction) => {
            return {
                zIndex: 0,
                x: direction < 0 ? 1000 : -1000,
                opacity: 0
            };
        }
    };

    return (
        <div ref={carouselRef} className="relative w-full mb-12 scroll-mt-16">

            <AnimatePresence initial={false} custom={direction} mode="wait">
                <motion.div
                    key={activeIndex}
                    custom={direction}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                        x: { type: "spring", stiffness: 300, damping: 30 },
                        opacity: { duration: 0.2 }
                    }}
                    className="w-full"
                >
                    <Component />
                </motion.div>
            </AnimatePresence>

            <button
                onClick={() => navigate(-1)}
                className="fixed top-1/2 left-3 md:left-5 transform -translate-y-1/2 bg-white/60 hover:bg-white text-gray-700 hover:text-primary p-3 rounded-full shadow-lg z-20 transition-all duration-200 ease-in-out"
                aria-label="Previous Expertise"
            >
                <ChevronLeftIcon />
            </button>
            <button
                onClick={() => navigate(1)}
                className="fixed top-1/2 right-3 md:right-5 transform -translate-y-1/2 bg-white/60 hover:bg-white text-gray-700 hover:text-primary p-3 rounded-full shadow-lg z-20 transition-all duration-200 ease-in-out"
                aria-label="Next Expertise"
            >
                <ChevronRightIcon />
            </button>

            <div className="absolute bottom-[-30px] left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                {expertiseComponents.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => goToIndex(index)}
                        className={`w-3 h-3 rounded-full transition-colors duration-200 ease-in-out ${ 
                            activeIndex === index ? 'bg-primary' : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                        aria-label={`Go to section ${index + 1}`}
                    />
                ))}
            </div>
        </div>
    );
}

export default ExpertiseCarousel; 